import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import {
    collection, addDoc, getDocs, updateDoc, deleteDoc,
    doc, query, where, getDoc, serverTimestamp
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

// MUI Components
import {
    Paper, Typography, TextField, Button, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Pagination, CircularProgress, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, MenuItem, Autocomplete
} from '@mui/material';
import {
    Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon,
    Add as AddIcon, AccountBalance as BankIcon, Payments as SalaryIcon
} from '@mui/icons-material';

const PaymentAllocation = () => {
    const [allocations, setAllocations] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [schoolId, setSchoolId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);

    // Form State
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [basic, setBasic] = useState(0);
    const [hra, setHra] = useState(0);
    const [da, setDa] = useState(0);
    const [ta, setTa] = useState(0);
    const [paymentMode, setPaymentMode] = useState('Bank Transfer');
    const [bankName, setBankName] = useState('');
    const [accountNo, setAccountNo] = useState('');
    const [branch, setBranch] = useState('');
    const [ifsc, setIfsc] = useState('');

    // Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (userDoc.exists()) {
                    const sId = userDoc.data().schoolId;
                    setSchoolId(sId);
                    fetchInitialData(sId);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const fetchInitialData = async (sId) => {
        // 1. Define the list of roles you want to include
        const rolesToFetch = [
            "Admin", "Reception", "Accountant", "Library", "Teacher", "Store", "Hostel", "Canteen",
            "Driver"
        ];

        try {
            // 2. Use the 'in' operator to fetch all users matching these roles within the school
            const staffQuery = query(
                collection(db, "users"),
                where("schoolId", "==", sId),
                where("role", "in", rolesToFetch) // Fetches any user where role is in the array
            );

            const staffSnap = await getDocs(staffQuery);

            // 3. Map the data to your list
            const list = staffSnap.docs.map(doc => {
                const data = doc.data();
                // Using a fallback to 'Unknown' if the name field is missing
                const displayName = data.name || "No Name";

                return {
                    id: doc.id,
                    // Label includes role for better clarity in the dropdown
                    label: `${displayName} (${data.role})`.toUpperCase(),
                    name: displayName.toUpperCase(),
                    role: data.role
                };
            });

            setStaffList(list);
            fetchAllocations(sId);

        } catch (error) {
            console.error("Error fetching staff data:", error);
        }
    };

    const fetchAllocations = async (sId) => {
        const q = query(collection(db, "salaryAllocations"), where("schoolId", "==", sId));
        const snap = await getDocs(q);
        setAllocations(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    const totalSalary = Number(basic) + Number(hra) + Number(da) + Number(ta);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedStaff) return alert("Please select staff");
        setSaving(true);

        const payload = {
            staffId: selectedStaff.id,
            staffName: selectedStaff.name,
            basic: Number(basic),
            hra: Number(hra),
            da: Number(da),
            ta: Number(ta),
            totalSalary,
            paymentMode,
            bankName,
            accountNo,
            branch,
            ifsc,
            schoolId,
            updatedAt: serverTimestamp()
        };

        try {
            if (editId) {
                await updateDoc(doc(db, "salaryAllocations", editId), payload);
            } else {
                await addDoc(collection(db, "salaryAllocations"), { ...payload, createdAt: serverTimestamp() });
            }
            handleClose();
            fetchAllocations(schoolId);
        } catch (err) { console.error(err); }
        setSaving(false);
    };

    const handleClose = () => {
        setOpen(false); setEditId(null); setSelectedStaff(null);
        setBasic(0); setHra(0); setDa(0); setTa(0);
        setBankName(''); setAccountNo(''); setBranch(''); setIfsc('');
        setPaymentMode('Bank Transfer');
    };

    const startEdit = (row) => {
        setEditId(row.id);
        const stf = staffList.find(s => s.id === row.staffId);
        setSelectedStaff(stf || { id: row.staffId, label: row.staffName, name: row.staffName });
        setBasic(row.basic); setHra(row.hra); setDa(row.da); setTa(row.ta);
        setBankName(row.bankName); setAccountNo(row.accountNo); setBranch(row.branch); setIfsc(row.ifsc);
        setPaymentMode(row.paymentMode);
        setOpen(true);
    };

    const filtered = allocations.filter(a => (a.staffName?.toLowerCase() || "").includes(searchTerm.toLowerCase()));
    const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    if (loading) return <Box sx={{ textAlign: 'center', mt: 10 }}><CircularProgress /></Box>;

    return (
        <Grid sx={{ mt: 2, justifyContent: 'center' }}>
            <Grid item xs={12} md={11}>
                <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
                    <Grid className="row" alignItems="center" sx={{ mb: 3 }}>
                        <Grid className="col-sm-6" item xs={6}><Typography variant="h5" fontWeight="bold" color="primary">Salary & Payment Allocation</Typography></Grid>
                        <Grid className="col-sm-6" item xs={6} sx={{ textAlign: 'right' }}>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Allocate Salary</Button>
                        </Grid>
                    </Grid>

                    <TextField size="small" placeholder="Search staff name..." fullWidth onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 3, maxWidth: 400 }} InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'gray' }} /> }} />

                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell><b>Sr.No</b></TableCell>
                                    <TableCell><b>Staff Name</b></TableCell>
                                    <TableCell><b>Basic</b></TableCell>
                                    <TableCell><b>Total Salary</b></TableCell>
                                    <TableCell><b>Payment Mode</b></TableCell>
                                    <TableCell align="right"><b>Actions</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginated.map((row, i) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{(page - 1) * itemsPerPage + i + 1}</TableCell>
                                        <TableCell>{row.staffName}</TableCell>
                                        <TableCell>₹{row.basic}</TableCell>
                                        <TableCell><b>₹{row.totalSalary}</b></TableCell>
                                        <TableCell>{row.paymentMode}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" color="primary" onClick={() => startEdit(row)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" onClick={async () => { if (window.confirm("Delete allocation?")) { await deleteDoc(doc(db, "salaryAllocations", row.id)); fetchAllocations(schoolId); } }}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination count={Math.ceil(filtered.length / itemsPerPage)} sx={{ mt: 2, display: 'flex', justifyContent: 'center' }} onChange={(e, v) => setPage(v)} />
                </Paper>
            </Grid>

            {/* Allocation Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontWeight: 'bold' }}>{editId ? 'Update Salary Structure' : 'New Salary Allocation'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Grid className="row" spacing={2}>
                            <Grid className="col-sm-6 mb-3" item xs={12} sm={8}>
                                <Autocomplete
                                    options={staffList}
                                    getOptionLabel={(option) => option.label || ""}
                                    value={selectedStaff}
                                    onChange={(e, val) => setSelectedStaff(val)}
                                    renderInput={(params) => <TextField {...params} label="Select Staff Member" size="small" required />}
                                />
                            </Grid>
                            <Grid className="col-sm-6 mb-3" item xs={12} sm={4}>
                                <TextField select fullWidth label="Payment Mode" size="small" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                                    <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                                    <MenuItem value="Cash">Cash</MenuItem>
                                    <MenuItem value="Cheque">Cheque</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid className="col-sm-12 mb-3" item xs={12}><Divider><Typography variant="caption" color="primary" fontWeight="bold">SALARY BREAKUP</Typography></Divider></Grid>

                            <Grid className="col-sm-6 mb-3" item xs={6} sm={3}><TextField fullWidth label="Basic Salary" type="number" size="small" required value={basic} onChange={(e) => setBasic(e.target.value)} /></Grid>
                            <Grid className="col-sm-6 mb-3" item xs={6} sm={3}><TextField fullWidth label="HRA" type="number" size="small" value={hra} onChange={(e) => setHra(e.target.value)} /></Grid>
                            <Grid className="col-sm-6 mb-3" item xs={6} sm={3}><TextField fullWidth label="DA" type="number" size="small" value={da} onChange={(e) => setDa(e.target.value)} /></Grid>
                            <Grid className="col-sm-6 mb-3" item xs={6} sm={3}><TextField fullWidth label="TA" type="number" size="small" value={ta} onChange={(e) => setTa(e.target.value)} /></Grid>

                            <Grid className="col-sm-6 mb-3" item xs={12}>
                                <Paper sx={{ p: 1.5, bgcolor: '#e3f2fd', textAlign: 'right' }} variant="outlined">
                                    <Typography variant="subtitle1">Gross Salary: <b>₹{totalSalary}</b></Typography>
                                </Paper>
                            </Grid>

                            {paymentMode === 'Bank Transfer' && (
                                <>
                                    <Grid className="col-sm-12 mb-3" item xs={12}><Divider><Typography variant="caption" color="primary" fontWeight="bold">BANK ACCOUNT DETAILS</Typography></Divider></Grid>
                                    <Grid className="col-sm-6 mb-3" item xs={12} sm={6}><TextField fullWidth label="Bank Name" size="small" value={bankName} onChange={(e) => setBankName(e.target.value)} /></Grid>
                                    <Grid className="col-sm-6 mb-3" item xs={12} sm={6}><TextField fullWidth label="Account Number" size="small" value={accountNo} onChange={(e) => setAccountNo(e.target.value)} /></Grid>
                                    <Grid className="col-sm-6 mb-3" item xs={12} sm={6}><TextField fullWidth label="Branch Name" size="small" value={branch} onChange={(e) => setBranch(e.target.value)} /></Grid>
                                    <Grid className="col-sm-6 mb-3" item xs={12} sm={6}><TextField fullWidth label="IFSC Code" size="small" value={ifsc} onChange={(e) => setIfsc(e.target.value)} /></Grid>
                                </>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save Allocation'}</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Grid>
    );
};

export default PaymentAllocation;